import * as React from 'react';
import { CheckCircle2, Lock } from 'lucide-react';

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { formatDateHeure, formatDateLongue } from '../../lib/formats';
import { libelleMois, premierDuMoisSuivant } from './reportHelpers';

/**
 * État de clôture du mois, compact, dans la bande du mois.
 *
 * - mois clôturé: pastille « Clôturé le … », et, s'il y a eu des saisies après
 *   la clôture, un avertissement (sans aucun blocage);
 * - mois terminé mais pas clôturé: bouton « Clôturer le mois » avec confirmation;
 * - mois en cours: pastille « Mois en cours » et la date à partir de laquelle
 *   la clôture sera possible.
 */
const ClosureBanner = ({ periode, statut, chargement, onCloturer, enCours }) => {
  const [confirmation, setConfirmation] = React.useState(false);
  const mois = libelleMois(periode);
  const moisMajuscule = mois.charAt(0).toUpperCase() + mois.slice(1);

  if (!statut) {
    return chargement ? (
      <p className="text-sm text-muted-foreground" role="status">
        Vérification de l'état du mois…
      </p>
    ) : null;
  }

  if (statut.closed) {
    return (
      <div className="flex items-center gap-2 text-sm" data-testid="etat-mois">
        <Badge
          variant="success"
          className="gap-1.5"
          title={statut.source === 'planificateur' ? 'Clôture automatique' : 'Clôture faite à la main'}
        >
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          Clôturé le {formatDateHeure(statut.closed_at)}
        </Badge>
      </div>
    );
  }

  if (!statut.closable) {
    return (
      <div className="flex min-w-0 items-center gap-2 text-sm" data-testid="etat-mois">
        <Badge
          variant="info"
          className="shrink-0 gap-1.5"
          title={`Clôture possible dès le ${formatDateLongue(premierDuMoisSuivant(periode))}`}
        >
          <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          Mois en cours
        </Badge>
        <p className="hidden min-w-0 text-xs text-muted-foreground 2xl:block">
          Clôture possible dès le {formatDateLongue(premierDuMoisSuivant(periode))}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2 text-sm" data-testid="etat-mois">
        <Badge variant="warning" className="shrink-0" title={`${moisMajuscule} est terminé. La clôture fige le bilan et la synthèse du mois.`}>
          À clôturer
        </Badge>
        <Button type="button" size="sm" onClick={() => setConfirmation(true)} disabled={enCours}>
          <Lock aria-hidden="true" />
          Clôturer le mois
        </Button>
      </div>

      <AlertDialog open={confirmation} onOpenChange={setConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clôturer {mois} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le bilan, la synthèse par camion et les camions loués de {mois} seront figés tels
              qu'ils sont aujourd'hui. Vous pourrez toujours saisir des opérations de ce mois : elles
              seront simplement signalées comme faites après la clôture.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmation(false);
                onCloturer();
              }}
            >
              Clôturer {mois}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ClosureBanner;
